/**
 * JSON Editor Component
 * Provides a syntax-aware JSON input field with validation.
 * Used for: AIFunction Parameters, KnowledgeBase Configuration,
 *           Agent Capability Parameters, Agent Version snapshots
 */

import React, { useState } from 'react';
import './JsonEditor.css';
import { JsonViewer } from './JsonViewer';

export interface JsonEditorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  height?: string;
  placeholder?: string;
  label?: string;
  showFormatButton?: boolean;
  showCopyButton?: boolean;
  showValidationStatus?: boolean;
  /** Adds right-aligned Edit | Tree tab toggle above the textarea */
  showTreeView?: boolean;
  /** Stretch to fill a flex-column parent (e.g. modal body). Ignores the height prop. */
  fill?: boolean;
}

export const JsonEditor: React.FC<JsonEditorProps> = ({
  value,
  onChange,
  disabled = false,
  height = '300px',
  placeholder = 'Enter valid JSON...',
  label,
  showFormatButton = true,
  showCopyButton = false,
  showValidationStatus = true,
  showTreeView = false,
  fill = false,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(true);
  const [copied, setCopied] = useState(false);
  const [viewMode, setViewMode] = useState<'edit' | 'tree'>('edit');

  const validateJson = (json: string): boolean => {
    if (!json.trim()) {
      setError(null);
      setIsValid(true);
      return true;
    }
    try {
      JSON.parse(json);
      setError(null);
      setIsValid(true);
      return true;
    } catch (err: any) {
      setError(err.message);
      setIsValid(false);
      return false;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    validateJson(newValue);
  };

  const handleBlur = () => {
    validateJson(value);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(value);
      const formatted = JSON.stringify(parsed, null, 2);
      onChange(formatted);
      setError(null);
      setIsValid(true);
    } catch (err: any) {
      setError('Cannot format: ' + err.message);
      setIsValid(false);
    }
  };

  const hasTopbar = showTreeView || showCopyButton || showFormatButton || showValidationStatus;

  return (
    <div className={`json-editor-container${fill ? ' json-editor-fill' : ''}`}>
      {label && <label className="json-editor-label">{label}</label>}

      <div className="json-editor-wrapper">
        {/* Single toolbar: tabs left, status + buttons right */}
        {hasTopbar && (
          <div className="json-editor-topbar">
            <div className="json-editor-tab-group">
              {showTreeView && (
                <>
                  <button
                    type="button"
                    className={`json-view-tab${viewMode === 'edit' ? ' active' : ''}`}
                    onClick={() => setViewMode('edit')}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className={`json-view-tab${viewMode === 'tree' ? ' active' : ''}`}
                    onClick={() => setViewMode('tree')}
                  >
                    Tree
                  </button>
                </>
              )}
            </div>

            <div className="json-editor-actions">
              {showValidationStatus && viewMode === 'edit' && error && (
                <span className="json-error" title={error}>
                  ⚠ {error.length > 50 ? error.slice(0, 50) + '…' : error}
                </span>
              )}
              {showValidationStatus && viewMode === 'edit' && isValid && value.trim() && (
                <span className="json-valid">✓ Valid</span>
              )}
              {showCopyButton && (
                <button
                  type="button"
                  className="json-format-btn"
                  onClick={handleCopy}
                  disabled={disabled}
                  title="Copy to clipboard"
                >
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
              )}
              {showFormatButton && viewMode === 'edit' && (
                <button
                  type="button"
                  className="json-format-btn"
                  onClick={formatJson}
                  disabled={disabled}
                  title="Format JSON with proper indentation"
                >
                  Format
                </button>
              )}
            </div>
          </div>
        )}

        {viewMode === 'edit' ? (
          <textarea
            className={`json-editor-textarea${!isValid ? ' error' : ''}`}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={placeholder}
            style={fill ? undefined : { height }}
            spellCheck="false"
          />
        ) : (
          <JsonViewer value={value} maxHeight={fill ? undefined : height} />
        )}
      </div>
    </div>
  );
};

export default JsonEditor;
